import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Avatar,
  Chip,
  Icon,
  TextField,
  Grid,
  Divider,
} from "@mui/material";
import Africastalking from "africastalking-client";
import MDButton from "theme/components/MDButton";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";

export default function VoiceDialer({ getToken }) {
  const [state, setState] = useState("notready");
  const [isReady, setIsReady] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [timer, setTimer] = useState(0);
  const [wakeLock, setWakeLock] = useState(null);

  const ringtoneRef = useRef(null);
  const [afToken, setAFToken] = useState(null);
  const clientRef = useRef(null);

  // =========================
  // INIT CLIENT
  // =========================
  const STORAGE_KEY = "at_webrtc_token";

  const getStoredToken = () => {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (!data) return null;

      // add 1 min safety buffer
      if (Date.now() < data.expires - 60_000) {
        return data.token;
      }

      return null;
    } catch {
      return null;
    }
  };

  const saveToken = (token, expires) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        token,
        expires: new Date(expires).getTime(),
      }),
    );
  };

  useEffect(() => {
    let reconnectTimeout;

    const setupClient = (validToken) => {
      if (clientRef.current) {
        try {
          clientRef.current.destroy?.();
        } catch {}
      }

      const client = new Africastalking.Client(validToken);
      clientRef.current = client;

      client.on("ready", () => {
        setState("ready");
        setIsReady(true);
      });

      client.on("notready", () => {
        setState("notready");
        setIsReady(false);
      });

      client.on("calling", () => setState("calling"));

      client.on("incomingcall", () => {
        setState("incomingcall");
        ringtoneRef.current?.play().catch(() => {});
      });

      client.on("callaccepted", () => {
        setState("callaccepted");
        ringtoneRef.current?.pause();
        if (ringtoneRef.current) ringtoneRef.current.currentTime = 0;
      });

      client.on("hangup", () => {
        setState("hangup");
        ringtoneRef.current?.pause();
        if (ringtoneRef.current) ringtoneRef.current.currentTime = 0;

        setTimeout(() => setState("ready"), 1500);
      });

      const reconnect = () => {
        setState("offline");
        setIsReady(false);

        clearTimeout(reconnectTimeout);

        reconnectTimeout = setTimeout(() => {
          init(); // 🔁 re-init
        }, 2000);
      };

      client.on("offline", reconnect);
      client.on("closed", reconnect);
    };

    const init = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        let validToken = getStoredToken();

        // ❗ If no valid stored token → fetch from parent
        if (!validToken) {
          const res = await getToken();
          if (!res?.token || !res?.expires) {
            throw new Error("Failed to fetch valid token");
          }

          validToken = res.token;
          saveToken(res.token, res.expires);
        }

        setAFToken(validToken);
        setupClient(validToken);
      } catch (err) {
        console.error("Token init error:", err);

        // retry after delay
        setTimeout(init, 3000);
      }
    };

    init();

    return () => {
      clearTimeout(reconnectTimeout);
      clientRef.current?.destroy?.();
    };
  }, [getToken]);

  useEffect(() => {
    ringtoneRef.current = new Audio("/audio/002.mp3");
    ringtoneRef.current.loop = true;
  }, []);

  // =========================
  // WAKE LOCK
  // =========================
  const requestWakeLock = async () => {
    try {
      if ("wakeLock" in navigator) {
        const lock = await navigator.wakeLock.request("screen");
        setWakeLock(lock);
      }
    } catch {}
  };

  const releaseWakeLock = async () => {
    if (wakeLock) {
      await wakeLock.release();
      setWakeLock(null);
    }
  };

  useEffect(() => {
    if (state === "calling" || state === "callaccepted") {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
  }, [state]);

  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.visibilityState === "visible" &&
        (state === "calling" || state === "callaccepted")
      ) {
        requestWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [state]);

  // =========================
  // TIMER
  // =========================
  useEffect(() => {
    let interval;
    if (state === "callaccepted") {
      interval = setInterval(() => setTimer((t) => t + 1), 1000);
    } else {
      setTimer(0);
    }
    return () => clearInterval(interval);
  }, [state]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  // =========================
  // ACTIONS (GUARDED ONLY)
  // =========================
  const handleCall = () => {
    if (!isReady) return;
    if (!phoneNumber || !clientRef.current) return;
    clientRef.current?.call(phoneNumber);
  };

  const handleHangup = () => {
    if (!isReady) return;
    clientRef.current?.hangup();
  };

  const handleAccept = () => {
    if (!isReady) return;
    clientRef.current?.answer();
  };

  const handleReject = () => {
    if (!isReady) return;
    clientRef.current?.hangup();
  };

  const config = {
    ready: { label: "Ready", color: "success", icon: "call" },
    notready: { label: "Not Ready", color: "default", icon: "phone_disabled" },
    calling: { label: "Calling...", color: "success", icon: "call" },
    incomingcall: {
      label: "Incoming Call",
      color: "info",
      icon: "call_received",
    },
    callaccepted: { label: "In Call", color: "success", icon: "call" },
    hangup: { label: "Call Ended", color: "error", icon: "call_end" },
    offline: { label: "Offline", color: "error", icon: "wifi_off" },
    closed: { label: "Disconnected", color: "default", icon: "wifi_off" },
  }[state];

  return (
    <Box sx={{ width: "100%", maxWidth: 420, mx: "auto" }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 4, textAlign: "center" }}>
        <MDBox display="flex" justifyContent="space-between" mb={1}>
          <MDBox mt={0.5}>
            <MDTypography variant="h6" fontWeight="regular">
              Dialer Status:
            </MDTypography>
          </MDBox>
          <MDBox>
            <Chip label={config.label} color={config.color} />
          </MDBox>
        </MDBox>
        <MDBox mb={3}>
            <Divider />
        </MDBox>
        <Stack spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Enter number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            fullWidth
          />

          {state === "callaccepted" && (
            <Typography color="success.main">{formatTime(timer)}</Typography>
          )}

          {/* OUTGOING */}
          {state === "calling" && (
            <Stack spacing={1} alignItems="center">
              <Avatar sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                <Icon>person</Icon>
              </Avatar>

              <Icon sx={{ animation: "moveUp 1s infinite" }}>arrow_upward</Icon>

              <Avatar sx={{ width: 56, height: 56, bgcolor: "success.main" }}>
                <Icon>call</Icon>
              </Avatar>
            </Stack>
          )}

          {/* INCOMING */}
          {state === "incomingcall" && (
            <Stack spacing={1} alignItems="center">
              <Avatar sx={{ width: 56, height: 56, bgcolor: "success.main" }}>
                <Icon>call</Icon>
              </Avatar>

              <Icon sx={{ animation: "moveDown 1s infinite" }}>
                arrow_downward
              </Icon>

              <Avatar sx={{ width: 48, height: 48, bgcolor: "primary.main" }}>
                <Icon>support_agent</Icon>
              </Avatar>

              <Typography mt={1} variant="body2">
                {config.label}
              </Typography>
            </Stack>
          )}

          {/* CONNECTED */}
          {state === "callaccepted" && (
            <Box sx={{ position: "relative", width: 80, height: 80 }}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  transform: "translate(-50%, -50%)",
                  animation: "ripple 2s infinite",
                  opacity: 0.2,
                }}
              />
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: "success.main",
                  color: "#fff",
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <Icon>call</Icon>
              </Avatar>
            </Box>
          )}

          {/* DEFAULT */}
          {state !== "calling" &&
            state !== "incomingcall" &&
            state !== "callaccepted" && (
              <Avatar sx={{ width: 64, height: 64 }}>
                <Icon>{config.icon}</Icon>
              </Avatar>
            )}

          {/* ACTIONS */}
          <Stack spacing={1} width="100%" mt={3}>
            <MDBox mt={3}>
              {state === "ready" && (
                <MDButton
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleCall}
                  disabled={!isReady}
                >
                  Call
                </MDButton>
              )}

              {state === "calling" && (
                <MDButton
                  variant="contained"
                  fullWidth
                  color="error"
                  onClick={handleHangup}
                >
                  Cancel
                </MDButton>
              )}

              {state === "incomingcall" && (
                <Grid container spacing={3}>
                  <Grid item xs={6} md={6} lg={6}>
                    <MDButton
                      variant="contained"
                      fullWidth
                      color="primary"
                      onClick={handleAccept}
                    >
                      Accept
                    </MDButton>
                  </Grid>
                  <Grid item xs={6} md={6} lg={6}>
                    <MDButton
                      variant="outlined"
                      fullWidth
                      color="error"
                      onClick={handleReject}
                    >
                      Reject
                    </MDButton>
                  </Grid>
                </Grid>
              )}

              {state === "callaccepted" && (
                <MDButton
                  variant="contained"
                  fullWidth
                  color="error"
                  onClick={handleHangup}
                >
                  Hang Up
                </MDButton>
              )}
            </MDBox>
          </Stack>
        </Stack>
      </Paper>

      <style>
        {`
          @keyframes moveUp {
            0% { transform: translateY(10px); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateY(-10px); opacity: 0; }
          }

          @keyframes moveDown {
            0% { transform: translateY(-10px); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateY(10px); opacity: 0; }
          }

          @keyframes ripple {
            0% { transform: translate(-50%, -50%) scale(0.8); opacity: 0.4; }
            70% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
            100% { opacity: 0; }
          }
        `}
      </style>
    </Box>
  );
}
